import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { colors, fonts } from '../../components/annuaire/designSystem';
import { CODE_RECENT_S, DELAI_RENVOI_S, lireRefusCode } from './codeEmail';
import {
  BoutonVert,
  Corps,
  Encart,
  Icone,
  Pied,
  SousTitre,
  Titre,
  icone,
} from './ui';

/**
 * L'écran du code, seulement en réservation instantanée sur `/reserver/:slug`
 * (maquette validée le 23/09, `widget-code-email.html`, contrat de
 * FIXERLIGHT-056 lot 2). Le client a laissé ses coordonnées ; l'api lui a écrit
 * un code à 6 chiffres. L'horaire n'est confirmé qu'à la saisie du bon code, et
 * il n'est PAS retenu d'ici là : un autre client peut le prendre (`slot_taken`),
 * la page ne promet donc jamais de le garder.
 *
 * Validation dès le sixième chiffre, avec le bouton en secours. Un seul vrai
 * champ (collage et remplissage automatique du code reçu par SMS ou par le
 * clavier fonctionnent), habillé de six cases.
 *
 * Le courriel du code peut ne jamais arriver (FIXERLIGHT-807). Passé le délai
 * de renvoi, l'écran propose d'envoyer une simple demande : elle est déjà
 * enregistrée chez l'atelier, qui la confirmera lui-même. Quand c'est l'api
 * qui a renoncé au code, l'écran suit sans que le client ait à le demander.
 */

const Centre = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-top: 12px;
  text-align: center;
`;

const Cases = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  gap: 8px;
  margin: 10px 0 4px;

  input {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    border: 0;
    background: transparent;
    color: transparent;
    caret-color: transparent;
    /* 16px minimum : en dessous, iOS zoome à la mise au point. */
    font-size: 16px;
    outline: none;
    cursor: text;
  }
`;

const Case = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 46px;
  height: 56px;
  border: 2px solid
    ${(p) =>
      p.$erreur ? '#c0391b' : p.$actif ? colors.textDark : colors.borderLight};
  border-radius: 14px;
  background: ${colors.white};
  font-family: ${fonts.heading};
  font-size: 24px;
  color: ${colors.textDark};

  @media (max-width: 360px) {
    width: 40px;
  }
`;

const Renvoi = styled.p`
  margin: 6px 0 0;
  font-size: 13.5px;
  color: ${colors.textSecondary};

  button {
    padding: 0;
    border: none;
    background: none;
    font: inherit;
    font-weight: 600;
    color: ${colors.textDark};
    text-decoration: underline;
    text-underline-offset: 2px;
    cursor: pointer;
  }
  button:disabled {
    color: ${colors.textSecondary};
    text-decoration: none;
    cursor: default;
  }
  button:focus-visible {
    outline: 3px solid ${colors.textDark};
    outline-offset: 2px;
    border-radius: 2px;
  }
`;

const Horaire = styled.p`
  margin: 14px 0 0;
  padding: 12px 14px;
  border-radius: 14px;
  background: ${colors.grisClair};
  font-size: 13.5px;
  line-height: 1.45;
  text-align: left;
`;

export default function EtapeCode({
  email,
  horaire,
  onValider,
  onRenvoyer,
  onConfirme,
  onCreneauPris,
  onSansCode,
  titreRef,
}) {
  const [chiffres, setChiffres] = useState('');
  const [erreur, setErreur] = useState(null);
  const [info, setInfo] = useState(null);
  const [enCours, setEnCours] = useState(false);
  // Le code vient de partir : le renvoi est refusé pendant 60 s.
  const [attente, setAttente] = useState(DELAI_RENVOI_S);
  // Le repli vers une simple demande, proposé une fois le délai de renvoi
  // écoulé, puis gardé : un client qui a déjà attendu n'attend pas une seconde
  // fois après un renvoi.
  const [repli, setRepli] = useState(false);
  const champ = useRef(null);
  const parti = useRef(false);
  // L'heure du dernier code parti, pour lire un `code_expired` : sur un code
  // récent, il dit que le code a été effacé, pas qu'il a fait son temps.
  const codePartiLe = useRef(Date.now());

  // Au départ, et à chaque fois qu'une vérification se termine : un champ
  // désactivé ne peut pas prendre le curseur.
  useEffect(() => {
    if (!enCours && champ.current) champ.current.focus();
  }, [enCours]);

  useEffect(() => {
    if (attente <= 0) {
      setRepli(true);
      return undefined;
    }
    const minuteur = setTimeout(() => setAttente((a) => a - 1), 1000);
    return () => clearTimeout(minuteur);
  }, [attente]);

  const soumettre = (code) => {
    if (parti.current) return;
    parti.current = true;
    setEnCours(true);
    setErreur(null);
    setInfo(null);
    Promise.resolve()
      .then(() => onValider(code))
      .then(() => onConfirme())
      .catch((error) => {
        const refus = lireRefusCode(error, {
          codeRecent: Date.now() - codePartiLe.current < CODE_RECENT_S * 1000,
        });
        if (refus.creneauPris) {
          onCreneauPris();
          return;
        }
        if (refus.sansCode) {
          onSansCode();
          return;
        }
        setErreur(refus.message);
        setChiffres('');
        if (champ.current) champ.current.focus();
      })
      .finally(() => {
        parti.current = false;
        setEnCours(false);
      });
  };

  const changer = (valeur) => {
    if (parti.current) return;
    const propres = valeur.replace(/\D/g, '').slice(0, 6);
    setChiffres(propres);
    setErreur(null);
    if (propres.length === 6) soumettre(propres);
  };

  const renvoyer = () => {
    if (attente > 0 || parti.current) return;
    setErreur(null);
    setInfo(null);
    Promise.resolve()
      .then(() => onRenvoyer())
      .then(() => {
        codePartiLe.current = Date.now();
        setAttente(DELAI_RENVOI_S);
        setChiffres('');
        setInfo('Un nouveau code vient de partir.');
        if (champ.current) champ.current.focus();
      })
      .catch((error) => {
        const refus = lireRefusCode(error, { renvoi: true });
        if (refus.sansCode) {
          onSansCode();
          return;
        }
        if (refus.attente) setAttente(refus.attente);
        setErreur(refus.message);
      });
  };

  return (
    <>
      <Corps>
        <Centre>
          <Icone
            src={icone('MSG_MAIL')}
            alt=""
            $taille={64}
            style={{ margin: '0 auto' }}
          />
          <Titre tabIndex={-1} ref={titreRef} style={{ textAlign: 'center' }}>
            Vérifiez votre email
          </Titre>
          <SousTitre style={{ textAlign: 'center', margin: 0 }}>
            Un code à 6 chiffres a été envoyé à <b>{email}</b>. Il confirme que
            c’est bien vous, avant que votre rendez-vous soit confirmé.
          </SousTitre>
          <Cases>
            {Array.from({ length: 6 }, (_, i) => (
              <Case
                key={i}
                aria-hidden="true"
                $actif={i === chiffres.length && !enCours}
                $erreur={!!erreur}>
                {chiffres[i] || ''}
              </Case>
            ))}
            <input
              ref={champ}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="one-time-code"
              maxLength={6}
              aria-label="Code à 6 chiffres"
              aria-invalid={!!erreur}
              value={chiffres}
              disabled={enCours}
              onChange={(e) => changer(e.target.value)}
            />
          </Cases>
          {erreur && (
            <Encart $ton="alerte" role="alert">
              {erreur}
            </Encart>
          )}
          {info && !erreur && (
            <p role="status" style={{ margin: 0, fontSize: 13.5 }}>
              {info}
            </p>
          )}
          <Renvoi>
            {repli ? 'Vous n’avez rien reçu ?' : 'Rien reçu ?'}{' '}
            <button type="button" disabled={attente > 0} onClick={renvoyer}>
              {attente > 0
                ? `Renvoyer le code (${attente} s)`
                : 'Renvoyer le code'}
            </button>{' '}
            {repli ? (
              <>
                ou{' '}
                <button type="button" disabled={enCours} onClick={onSansCode}>
                  Envoyer une simple demande
                </button>
              </>
            ) : (
              '· valable 10 min'
            )}
          </Renvoi>
          {horaire && (
            <Horaire>
              <b>Horaire choisi : {horaire}.</b> Il est confirmé dès que le code
              est validé.
            </Horaire>
          )}
        </Centre>
      </Corps>
      <Pied>
        <BoutonVert
          type="button"
          disabled={enCours || chiffres.length < 6}
          onClick={() => soumettre(chiffres)}>
          {enCours ? 'Vérification…' : 'Confirmer mon rendez-vous'}
        </BoutonVert>
      </Pied>
    </>
  );
}
